/**
 * Slice 0 / AT-15 — classifier-side normalizer for canonical demographic
 * `user_attribute` records.
 *
 * The deterministic semantic-resolution gate. Turns raw LLM output (e.g.
 * `"Female"`, `"i'm married"`, `"around 80k"`) into canonical values
 * (`"F"`, `"married"`, `"80000"`) before the canonical store write. Failures
 * route to a structured `NormalizationOutcome` rejection with a discriminated
 * `reason` — never a silent slip-through.
 *
 * Per ADR 0005 §"Classifier-side normalization (Q4)":
 *   - `ruleId` is mandatory on success for downstream provenance audit.
 *   - The schema validator is invoked on the normalized value as a final
 *     check; failures route to `reason: "schema_mismatch"` (separate from
 *     `no_mapping`).
 *   - This module is the SINGLE semantic-resolution surface — `profileTier
 *     Formatter`, the reconciler, the wire-builder, and the wallet MUST NOT
 *     normalize. Drift here = double normalization.
 *
 * Scope: AT-15 covers the 10 initial-authored keys (`gender`, `marital_status`,
 * `age_range`, `income`, `property_type`, `ownership`, `number_of_children`,
 * `number_of_adults`, `location`, `language`). AT-19 adds the remaining 8
 * (`education`, `occupation`, `length_of_residence`, `life_stage`,
 * `median_home_value`, `monthly_housing_payment`, `number_of_individuals`,
 * `urbanization`) bringing the canonical vocabulary to 18 keys.
 *
 * @traces ADR 0005 Q3 + Q4, AT-15, AT-19
 */
import { CANONICAL_DEMOGRAPHIC_VALUE_SCHEMAS, } from './canonical-demographic-values.js';
function lower(s) {
    return s.trim().toLowerCase();
}
function strip(s) {
    // Strip non-alphanumeric except space/hyphen/plus to allow tolerance.
    return s
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9 +\-]/g, '')
        .replace(/\s+/g, ' ');
}
// gender ──────────────────────────────────────────────────────────────
const GENDER_RULES = [
    // Canonical pass-through (case-insensitive single-letter).
    { match: (l) => l === 'm', value: 'M', ruleId: 'gender:canonical-passthrough-m' },
    { match: (l) => l === 'f', value: 'F', ruleId: 'gender:canonical-passthrough-f' },
    { match: (l) => l === 'o', value: 'O', ruleId: 'gender:canonical-passthrough-o' },
    { match: (l) => l === 'u', value: 'U', ruleId: 'gender:canonical-passthrough-u' },
    // Common LLM variations (with trailing punctuation tolerance).
    { match: (l) => /^male\.?$/.test(l) || l === 'man', value: 'M', ruleId: 'gender:male' },
    { match: (l) => /^female\.?$/.test(l) || l === 'woman', value: 'F', ruleId: 'gender:female' },
    { match: (l) => l === 'other' || l === 'nonbinary' || l === 'non-binary' || l === 'enby', value: 'O', ruleId: 'gender:other' },
    {
        match: (l) => l === 'unknown' ||
            l === 'unspecified' ||
            l === 'prefer not to say' ||
            l === 'declined',
        value: 'U',
        ruleId: 'gender:unknown',
    },
];
// marital_status ──────────────────────────────────────────────────────
const MARITAL_RULES = [
    // Canonical pass-through.
    { match: (l) => l === 'single', value: 'single', ruleId: 'marital_status:canonical-single' },
    { match: (l) => l === 'married', value: 'married', ruleId: 'marital_status:canonical-married' },
    { match: (l) => l === 'divorced', value: 'divorced', ruleId: 'marital_status:canonical-divorced' },
    { match: (l) => l === 'widowed', value: 'widowed', ruleId: 'marital_status:canonical-widowed' },
    { match: (l) => l === 'separated', value: 'separated', ruleId: 'marital_status:canonical-separated' },
    { match: (l) => l === 'domestic_partner', value: 'domestic_partner', ruleId: 'marital_status:canonical-domestic_partner' },
    { match: (l) => l === 'unknown', value: 'unknown', ruleId: 'marital_status:canonical-unknown' },
    // Variations for "married".
    {
        match: (l) => l === "i'm married" ||
            l === 'im married' ||
            l === 'i am married' ||
            l === 'currently married',
        value: 'married',
        ruleId: 'marital_status:imm-prefix',
    },
    // Variations for "single".
    { match: (l) => l === 'never married' || l === 'unmarried', value: 'single', ruleId: 'marital_status:never-married' },
    // Variations for "domestic_partner".
    {
        match: (l) => l === 'domestic partner' ||
            l === 'cohabiting' ||
            l === 'cohabitating' ||
            l === 'living together' ||
            l === 'partnered',
        value: 'domestic_partner',
        ruleId: 'marital_status:cohabit-as-domestic-partner',
    },
    // Variations for "unknown".
    {
        match: (l) => l === 'prefer not to say' || l === 'declined' || l === 'undisclosed',
        value: 'unknown',
        ruleId: 'marital_status:declined',
    },
];
// age_range ───────────────────────────────────────────────────────────
function normalizeAgeRange(input) {
    const l = input.trim().toLowerCase();
    // Canonical pass-through (e.g. "18-24", "25-34", "65+").
    if (/^\d{2}-\d{2}$/.test(l))
        return { value: l, ruleId: 'age_range:canonical-passthrough' };
    if (l === '65+')
        return { value: '65+', ruleId: 'age_range:canonical-65-plus' };
    // "25 - 34" with spaces, "25 to 34", "25–34" (en-dash).
    const range = l.match(/^(\d{2})\s*(?:-|to|–|—)\s*(\d{2})$/);
    if (range) {
        return { value: `${range[1]}-${range[2]}`, ruleId: 'age_range:range-normalized' };
    }
    // "65 plus", "65 and over", "65 or older".
    if (/^65\s*(plus|\+|and over|or older|or more)$/.test(l)) {
        return { value: '65+', ruleId: 'age_range:65-plus-variation' };
    }
    return null;
}
// income ──────────────────────────────────────────────────────────────
function normalizeIncome(input) {
    const l = input.trim().toLowerCase();
    // Strip leading $ and commas.
    const stripped = l.replace(/[$,]/g, '').replace(/\s+/g, ' ').trim();
    // Canonical pass-through.
    if (/^\d+$/.test(stripped))
        return { value: stripped, ruleId: 'income:canonical-single' };
    if (/^\d+-\d+$/.test(stripped))
        return { value: stripped, ruleId: 'income:canonical-range' };
    if (/^\d+\+$/.test(stripped))
        return { value: stripped, ruleId: 'income:canonical-open' };
    // "50000 to 74999".
    const range = stripped.match(/^(\d+)\s*(?:to|-|–|—)\s*(\d+)$/);
    if (range) {
        return { value: `${range[1]}-${range[2]}`, ruleId: 'income:range-normalized' };
    }
    // "200000+" might appear after stripping.
    if (/^\d+\s*\+$/.test(stripped)) {
        return { value: stripped.replace(/\s+/g, ''), ruleId: 'income:open-normalized' };
    }
    // "around 80000", "80k", "approximately 80000".
    const approx = stripped.match(/^(?:around|approximately|approx|about|near|nearly)?\s*(\d+)(k)?$/);
    if (approx) {
        const base = approx[1];
        const k = approx[2];
        if (k) {
            return { value: String(parseInt(base, 10) * 1000), ruleId: 'income:k-suffix' };
        }
        return { value: base, ruleId: 'income:approx-prefix' };
    }
    return null;
}
// property_type ──────────────────────────────────────────────────────
const PROPERTY_TYPE_RULES = [
    { match: (l) => l === 'apartment' || l === 'flat' || l === 'apt', value: 'apartment', ruleId: 'property_type:apartment' },
    {
        match: (l) => l === 'house' ||
            l === 'single-family' ||
            l === 'single family' ||
            l === 'detached' ||
            l === 'detached house' ||
            l === 'home',
        value: 'house',
        ruleId: 'property_type:house',
    },
    { match: (l) => l === 'condo' || l === 'condominium', value: 'condo', ruleId: 'property_type:condo' },
    { match: (l) => l === 'townhouse' || l === 'town house' || l === 'town home' || l === 'townhome' || l === 'row house', value: 'townhouse', ruleId: 'property_type:townhouse' },
    { match: (l) => l === 'other', value: 'other', ruleId: 'property_type:other' },
];
// ownership ──────────────────────────────────────────────────────────
const OWNERSHIP_RULES = [
    { match: (l) => l === 'own' || l === 'owned' || l === 'owner' || l === 'homeowner' || l === 'home owner', value: 'own', ruleId: 'ownership:own' },
    { match: (l) => l === 'rent' || l === 'rented' || l === 'renter' || l === 'leasing' || l === 'tenant', value: 'rent', ruleId: 'ownership:rent' },
    { match: (l) => l === 'other', value: 'other', ruleId: 'ownership:other' },
];
// number_of_children / number_of_adults ──────────────────────────────
const WORD_TO_NUM = {
    zero: '0',
    none: '0',
    'no children': '0',
    one: '1',
    two: '2',
    three: '3',
    four: '4',
    five: '5+',
    six: '5+',
    seven: '5+',
    eight: '5+',
    nine: '5+',
    ten: '5+',
    'five or more': '5+',
    'five+': '5+',
};
function normalizeChildCount(input) {
    const l = input.trim().toLowerCase();
    if (l in WORD_TO_NUM) {
        return { value: WORD_TO_NUM[l], ruleId: 'number_of_children:word-to-num' };
    }
    if (/^\d+$/.test(l)) {
        const n = parseInt(l, 10);
        if (n >= 5)
            return { value: '5+', ruleId: 'number_of_children:cap-5-plus' };
        return { value: l, ruleId: 'number_of_children:canonical-passthrough' };
    }
    if (l === '5+')
        return { value: '5+', ruleId: 'number_of_children:canonical-5-plus' };
    return null;
}
function normalizeAdultCount(input) {
    const l = input.trim().toLowerCase();
    if (l in WORD_TO_NUM) {
        return { value: WORD_TO_NUM[l], ruleId: 'number_of_adults:word-to-num' };
    }
    if (/^\d+$/.test(l)) {
        const n = parseInt(l, 10);
        if (n >= 5)
            return { value: '5+', ruleId: 'number_of_adults:cap-5-plus' };
        return { value: l, ruleId: 'number_of_adults:canonical-passthrough' };
    }
    if (l === '5+')
        return { value: '5+', ruleId: 'number_of_adults:canonical-5-plus' };
    return null;
}
// language ───────────────────────────────────────────────────────────
const LANGUAGE_MAP = {
    // Canonical ISO-639-1 (case-insensitive pass-through).
    en: 'en',
    es: 'es',
    fr: 'fr',
    de: 'de',
    zh: 'zh',
    ja: 'ja',
    pt: 'pt',
    it: 'it',
    ko: 'ko',
    ru: 'ru',
    ar: 'ar',
    hi: 'hi',
    // English names.
    english: 'en',
    spanish: 'es',
    'español': 'es',
    espanol: 'es',
    french: 'fr',
    'français': 'fr',
    francais: 'fr',
    german: 'de',
    deutsch: 'de',
    chinese: 'zh',
    mandarin: 'zh',
    japanese: 'ja',
    portuguese: 'pt',
    'português': 'pt',
    portugues: 'pt',
    italian: 'it',
    italiano: 'it',
    korean: 'ko',
    russian: 'ru',
    arabic: 'ar',
    hindi: 'hi',
};
function normalizeLanguage(input) {
    const l = input.trim().toLowerCase();
    if (l in LANGUAGE_MAP) {
        const value = LANGUAGE_MAP[l];
        return { value, ruleId: l === value ? 'language:canonical-passthrough' : 'language:name-to-iso' };
    }
    return null;
}
// location ───────────────────────────────────────────────────────────
function normalizeLocation(input) {
    const trimmed = input.trim();
    if (trimmed.length === 0)
        return null;
    return { value: trimmed, ruleId: 'location:passthrough-trimmed' };
}
// ────────────────────────────────────────────────────────────────────────
// AT-19 — remaining 8 follow-up keys.
// ────────────────────────────────────────────────────────────────────────
// education ──────────────────────────────────────────────────────────
const EDUCATION_RULES = [
    // Canonical pass-through (lower-case underscored codes).
    { match: (l) => l === 'high_school', value: 'high_school', ruleId: 'education:canonical-high_school' },
    { match: (l) => l === 'some_college', value: 'some_college', ruleId: 'education:canonical-some_college' },
    { match: (l) => l === 'associate', value: 'associate', ruleId: 'education:canonical-associate' },
    { match: (l) => l === 'bachelor', value: 'bachelor', ruleId: 'education:canonical-bachelor' },
    { match: (l) => l === 'graduate', value: 'graduate', ruleId: 'education:canonical-graduate' },
    { match: (l) => l === 'postgraduate', value: 'postgraduate', ruleId: 'education:canonical-postgraduate' },
    { match: (l) => l === 'other', value: 'other', ruleId: 'education:canonical-other' },
    { match: (l) => l === 'unknown', value: 'unknown', ruleId: 'education:canonical-unknown' },
    // Common LLM variations: high school.
    {
        match: (l) => l === 'high school' ||
            l === 'high-school' ||
            l === 'highschool' ||
            l === 'hs' ||
            l === 'secondary' ||
            l === 'secondary school' ||
            l === 'ged',
        value: 'high_school',
        ruleId: 'education:high-school',
    },
    // Some college (no degree).
    {
        match: (l) => l === 'some college' || l === 'some-college' || l === 'college dropout' || l === 'partial college',
        value: 'some_college',
        ruleId: 'education:some-college',
    },
    // Associate degree.
    {
        match: (l) => l === 'associate' ||
            l === 'associates' ||
            l === "associate's" ||
            l === "associates degree" ||
            l === 'aa' ||
            l === 'as' ||
            l === 'aas',
        value: 'associate',
        ruleId: 'education:associate-degree',
    },
    // Bachelor degree.
    {
        match: (l) => l === 'bachelor' ||
            l === 'bachelors' ||
            l === "bachelor's" ||
            l === 'bachelors degree' ||
            l === 'undergraduate' ||
            l === 'undergrad' ||
            l === 'ba' ||
            l === 'bs' ||
            l === 'bsc' ||
            l === 'b.a.' ||
            l === 'b.s.',
        value: 'bachelor',
        ruleId: 'education:bachelor-degree',
    },
    // Graduate / Master's.
    {
        match: (l) => l === 'graduate' ||
            l === 'masters' ||
            l === "master's" ||
            l === 'masters degree' ||
            l === 'ma' ||
            l === 'ms' ||
            l === 'msc' ||
            l === 'm.a.' ||
            l === 'm.s.' ||
            l === 'mba',
        value: 'graduate',
        ruleId: 'education:masters-degree',
    },
    // Postgraduate / Doctorate.
    {
        match: (l) => l === 'postgraduate' ||
            l === 'post-graduate' ||
            l === 'phd' ||
            l === 'ph.d.' ||
            l === 'ph.d' ||
            l === 'doctorate' ||
            l === 'doctoral' ||
            l === 'jd' ||
            l === 'md',
        value: 'postgraduate',
        ruleId: 'education:postgraduate',
    },
    // Other / Unknown.
    {
        match: (l) => l === 'prefer not to say' || l === 'declined' || l === 'undisclosed' || l === 'unspecified',
        value: 'unknown',
        ruleId: 'education:declined',
    },
];
// occupation ─────────────────────────────────────────────────────────
function normalizeOccupation(input) {
    const trimmed = input.trim();
    if (trimmed.length === 0)
        return null;
    return { value: trimmed, ruleId: 'occupation:passthrough-trimmed' };
}
// length_of_residence ────────────────────────────────────────────────
function normalizeLengthOfResidence(input) {
    const l = input.trim().toLowerCase();
    // Canonical pass-through.
    if (/^\d+$/.test(l))
        return { value: l, ruleId: 'length_of_residence:canonical-single' };
    if (/^\d+-\d+$/.test(l))
        return { value: l, ruleId: 'length_of_residence:canonical-range' };
    if (l === '10+')
        return { value: '10+', ruleId: 'length_of_residence:canonical-10-plus' };
    // "3 to 5", "3-5 years".
    const range = l.match(/^(\d+)\s*(?:-|to|–|—)\s*(\d+)(?:\s*years?)?$/);
    if (range) {
        return { value: `${range[1]}-${range[2]}`, ruleId: 'length_of_residence:range-normalized' };
    }
    // "3 years", "5 yrs".
    const single = l.match(/^(\d+)\s*(?:years?|yrs?)$/);
    if (single) {
        const n = parseInt(single[1], 10);
        if (n >= 10)
            return { value: '10+', ruleId: 'length_of_residence:cap-10-plus' };
        return { value: String(n), ruleId: 'length_of_residence:single-with-suffix' };
    }
    // "10+ years", "10 or more", "ten plus".
    if (/^10\s*\+?\s*(?:years?|yrs?)$/.test(l)) {
        return { value: '10+', ruleId: 'length_of_residence:10-plus-with-suffix' };
    }
    if (/^10\s*(?:or more|or older|plus|\+)\s*(?:years?|yrs?)?$/.test(l)) {
        return { value: '10+', ruleId: 'length_of_residence:10-plus-variation' };
    }
    if (l === 'ten plus' || l === 'ten or more' || l === 'ten years' || l === 'ten') {
        return { value: '10+', ruleId: 'length_of_residence:ten-word' };
    }
    return null;
}
// life_stage ─────────────────────────────────────────────────────────
const LIFE_STAGE_RULES = [
    // Canonical pass-through.
    { match: (l) => l === 'young_adult', value: 'young_adult', ruleId: 'life_stage:canonical-young_adult' },
    { match: (l) => l === 'established_adult', value: 'established_adult', ruleId: 'life_stage:canonical-established_adult' },
    { match: (l) => l === 'parent', value: 'parent', ruleId: 'life_stage:canonical-parent' },
    { match: (l) => l === 'empty_nester', value: 'empty_nester', ruleId: 'life_stage:canonical-empty_nester' },
    { match: (l) => l === 'retiree', value: 'retiree', ruleId: 'life_stage:canonical-retiree' },
    { match: (l) => l === 'student', value: 'student', ruleId: 'life_stage:canonical-student' },
    { match: (l) => l === 'other', value: 'other', ruleId: 'life_stage:canonical-other' },
    { match: (l) => l === 'unknown', value: 'unknown', ruleId: 'life_stage:canonical-unknown' },
    // Variations.
    { match: (l) => l === 'young adult' || l === 'young-adult' || l === 'youth', value: 'young_adult', ruleId: 'life_stage:young-adult' },
    { match: (l) => l === 'established adult' || l === 'established-adult' || l === 'mid-career' || l === 'mid career', value: 'established_adult', ruleId: 'life_stage:established-adult' },
    { match: (l) => l === 'parent' || l === 'parents' || l === 'new parent' || l === 'new parents', value: 'parent', ruleId: 'life_stage:parent-variation' },
    { match: (l) => l === 'empty nester' || l === 'empty nesters' || l === 'empty-nester', value: 'empty_nester', ruleId: 'life_stage:empty-nester' },
    { match: (l) => l === 'retiree' || l === 'retirees' || l === 'retired', value: 'retiree', ruleId: 'life_stage:retiree-variation' },
    { match: (l) => l === 'student' || l === 'students' || l === 'in school' || l === 'studying', value: 'student', ruleId: 'life_stage:student-variation' },
];
// median_home_value / monthly_housing_payment — shared numeric-money normalizer.
function normalizeMoney(input, key) {
    const l = input.trim().toLowerCase();
    // Strip leading $ and commas.
    const stripped = l.replace(/[$,]/g, '').replace(/\s+/g, ' ').trim();
    // Canonical pass-through.
    if (/^\d+$/.test(stripped))
        return { value: stripped, ruleId: `${key}:canonical-single` };
    if (/^\d+-\d+$/.test(stripped))
        return { value: stripped, ruleId: `${key}:canonical-range` };
    if (/^\d+\+$/.test(stripped))
        return { value: stripped, ruleId: `${key}:canonical-open` };
    // "200000 to 499999".
    const range = stripped.match(/^(\d+)\s*(?:to|-|–|—)\s*(\d+)$/);
    if (range) {
        return { value: `${range[1]}-${range[2]}`, ruleId: `${key}:range-normalized` };
    }
    // Trailing "+".
    if (/^\d+\s*\+$/.test(stripped)) {
        return { value: stripped.replace(/\s+/g, ''), ruleId: `${key}:open-normalized` };
    }
    // "around 300000", "300k", "1m", "1.5k".
    const approx = stripped.match(/^(?:around|approximately|approx|about|near|nearly)?\s*(\d+(?:\.\d+)?)(k|m)?$/);
    if (approx) {
        const baseStr = approx[1];
        const suffix = approx[2];
        if (suffix === 'k') {
            const n = Math.round(parseFloat(baseStr) * 1000);
            return { value: String(n), ruleId: `${key}:k-suffix` };
        }
        if (suffix === 'm') {
            const n = Math.round(parseFloat(baseStr) * 1000000);
            return { value: String(n), ruleId: `${key}:m-suffix` };
        }
        // No suffix — only accept integer base.
        if (/^\d+$/.test(baseStr)) {
            return { value: baseStr, ruleId: `${key}:approx-prefix` };
        }
    }
    return null;
}
// number_of_individuals ───────────────────────────────────────────────
const WORD_TO_INDIVIDUALS = {
    one: '1',
    two: '2',
    three: '3',
    four: '4',
    five: '5',
    six: '6',
    seven: '7',
    eight: '8',
    nine: '9',
    ten: '10+',
    eleven: '10+',
    twelve: '10+',
    'ten or more': '10+',
    'ten plus': '10+',
};
function normalizeIndividualCount(input) {
    const l = input.trim().toLowerCase();
    if (l in WORD_TO_INDIVIDUALS) {
        return { value: WORD_TO_INDIVIDUALS[l], ruleId: 'number_of_individuals:word-to-num' };
    }
    if (/^\d+$/.test(l)) {
        const n = parseInt(l, 10);
        if (n >= 10)
            return { value: '10+', ruleId: 'number_of_individuals:cap-10-plus' };
        return { value: l, ruleId: 'number_of_individuals:canonical-passthrough' };
    }
    if (l === '10+')
        return { value: '10+', ruleId: 'number_of_individuals:canonical-10-plus' };
    return null;
}
// urbanization ────────────────────────────────────────────────────────
const URBANIZATION_RULES = [
    // Canonical pass-through (case-insensitive).
    { match: (l) => l === 'urban', value: 'urban', ruleId: 'urbanization:canonical-urban' },
    { match: (l) => l === 'suburban', value: 'suburban', ruleId: 'urbanization:canonical-suburban' },
    { match: (l) => l === 'rural', value: 'rural', ruleId: 'urbanization:canonical-rural' },
    // Variations for urban.
    {
        match: (l) => l === 'city' ||
            l === 'in the city' ||
            l === 'inner city' ||
            l === 'metropolitan' ||
            l === 'metro' ||
            l === 'downtown' ||
            l === 'urbanised' ||
            l === 'urbanized',
        value: 'urban',
        ruleId: 'urbanization:in-the-city',
    },
    // Variations for suburban.
    {
        match: (l) => l === 'suburb' ||
            l === 'suburbs' ||
            l === 'in the suburbs' ||
            l === 'sub-urban' ||
            l === 'commuter belt',
        value: 'suburban',
        ruleId: 'urbanization:suburb-variation',
    },
    // Variations for rural.
    {
        match: (l) => l === 'countryside' ||
            l === 'in the country' ||
            l === 'country' ||
            l === 'farm' ||
            l === 'on a farm' ||
            l === 'village' ||
            l === 'remote',
        value: 'rural',
        ruleId: 'urbanization:countryside-variation',
    },
];
// ────────────────────────────────────────────────────────────────────────
// Schema validator (final-check gate).
// ────────────────────────────────────────────────────────────────────────
function validateAgainstSchema(value, schema) {
    if (schema.kind === 'enum')
        return schema.values.includes(value);
    if (schema.kind === 'regex')
        return new RegExp(schema.pattern).test(value);
    if (schema.kind === 'open')
        return value.length > 0 && value.length <= schema.maxLength;
    return false;
}
function applyRuleList(rules, normalizedRaw, originalRaw) {
    for (const r of rules) {
        if (r.match(normalizedRaw, originalRaw)) {
            const value = typeof r.value === 'function' ? r.value(normalizedRaw, originalRaw) : r.value;
            return { value, ruleId: r.ruleId };
        }
    }
    return null;
}
// ────────────────────────────────────────────────────────────────────────
// Public API.
// ────────────────────────────────────────────────────────────────────────
/**
 * The 10 keys covered by AT-15 (ADR 0005 Q3 initial-authored set). AT-19
 * extends the authored set to all 18 canonical keys via
 * `AUTHORED_KEYS` below; this constant is preserved for any downstream
 * import that pinned the AT-15 boundary.
 */
export const AT15_AUTHORED_KEYS = [
    'gender',
    'marital_status',
    'age_range',
    'income',
    'property_type',
    'ownership',
    'number_of_children',
    'number_of_adults',
    'location',
    'language',
];
/**
 * The 8 keys added by AT-19 (ADR 0005 Q3 follow-up authored set).
 */
export const AT19_AUTHORED_KEYS = [
    'education',
    'occupation',
    'length_of_residence',
    'life_stage',
    'median_home_value',
    'monthly_housing_payment',
    'number_of_individuals',
    'urbanization',
];
/**
 * The full 18-key canonical demographic vocabulary (AT-15 + AT-19). The
 * normalizer's `unauthored_key` gate consults this set; downstream wallet
 * validation also enforces presence of a synced policy schema for each.
 */
export const AUTHORED_KEYS = [
    ...AT15_AUTHORED_KEYS,
    ...AT19_AUTHORED_KEYS,
];
/**
 * Deterministic post-LLM normalization gate. Maps raw LLM output to a
 * canonical value that passes the disclosure-policy schema for the key, or
 * routes the input to a structured drop with a discriminated reason.
 *
 * SAFETY: this is the ONE semantic-resolution surface in the pipeline.
 * Downstream code (reconciler, wire-builder, profileTierFormatter, wallet)
 * MUST NOT re-normalize — they consume canonical values verbatim.
 */
export function normalizeUserAttribute(input) {
    const { key } = input;
    const raw = typeof input.raw === 'string' ? input.raw : '';
    // Authored-key gate (AT-15 + AT-19 = 18 keys).
    if (!AUTHORED_KEYS.includes(key)) {
        return { ok: false, key, raw, reason: 'unauthored_key' };
    }
    // Empty / whitespace input.
    if (!raw.trim()) {
        return { ok: false, key, raw, reason: 'empty_raw' };
    }
    const lowered = lower(raw);
    const stripped = strip(raw);
    let mapped = null;
    switch (key) {
        case 'gender': {
            mapped = applyRuleList(GENDER_RULES, lowered, raw) ?? applyRuleList(GENDER_RULES, stripped, raw);
            break;
        }
        case 'marital_status': {
            mapped = applyRuleList(MARITAL_RULES, lowered, raw) ?? applyRuleList(MARITAL_RULES, stripped, raw);
            break;
        }
        case 'age_range': {
            mapped = normalizeAgeRange(raw);
            break;
        }
        case 'income': {
            mapped = normalizeIncome(raw);
            break;
        }
        case 'property_type': {
            mapped = applyRuleList(PROPERTY_TYPE_RULES, lowered, raw) ?? applyRuleList(PROPERTY_TYPE_RULES, stripped, raw);
            break;
        }
        case 'ownership': {
            mapped = applyRuleList(OWNERSHIP_RULES, lowered, raw) ?? applyRuleList(OWNERSHIP_RULES, stripped, raw);
            break;
        }
        case 'number_of_children': {
            mapped = normalizeChildCount(raw);
            break;
        }
        case 'number_of_adults': {
            mapped = normalizeAdultCount(raw);
            // Re-tag ruleId with the proper key namespace.
            if (mapped) {
                mapped.ruleId = mapped.ruleId.replace(/^number_of_(children|adults):/, 'number_of_adults:');
            }
            break;
        }
        case 'location': {
            mapped = normalizeLocation(raw);
            break;
        }
        case 'language': {
            mapped = normalizeLanguage(raw);
            break;
        }
        // ─── AT-19 keys ──────────────────────────────────────────────────
        case 'education': {
            mapped = applyRuleList(EDUCATION_RULES, lowered, raw) ?? applyRuleList(EDUCATION_RULES, stripped, raw);
            break;
        }
        case 'occupation': {
            mapped = normalizeOccupation(raw);
            break;
        }
        case 'length_of_residence': {
            mapped = normalizeLengthOfResidence(raw);
            break;
        }
        case 'life_stage': {
            mapped = applyRuleList(LIFE_STAGE_RULES, lowered, raw) ?? applyRuleList(LIFE_STAGE_RULES, stripped, raw);
            break;
        }
        case 'median_home_value': {
            mapped = normalizeMoney(raw, 'median_home_value');
            break;
        }
        case 'monthly_housing_payment': {
            mapped = normalizeMoney(raw, 'monthly_housing_payment');
            break;
        }
        case 'number_of_individuals': {
            mapped = normalizeIndividualCount(raw);
            break;
        }
        case 'urbanization': {
            mapped = applyRuleList(URBANIZATION_RULES, lowered, raw) ?? applyRuleList(URBANIZATION_RULES, stripped, raw);
            break;
        }
    }
    if (!mapped) {
        return { ok: false, key, raw, reason: 'no_mapping' };
    }
    // Final check — the mapped value MUST satisfy the canonical schema.
    // This catches bugs where a rule produces a value the schema rejects
    // (e.g. an `open` field exceeding maxLength).
    const schema = CANONICAL_DEMOGRAPHIC_VALUE_SCHEMAS[key];
    if (!schema) {
        // Defense in depth: AT15_AUTHORED_KEYS guarantees the schema exists,
        // but if drift ever occurred, surface it as schema_mismatch rather
        // than a panic.
        return { ok: false, key, raw, reason: 'schema_mismatch', ruleId: mapped.ruleId };
    }
    if (!validateAgainstSchema(mapped.value, schema)) {
        return { ok: false, key, raw, reason: 'schema_mismatch', ruleId: mapped.ruleId };
    }
    return {
        ok: true,
        key,
        value: mapped.value,
        ruleId: mapped.ruleId,
    };
}
//# sourceMappingURL=normalize-user-attribute.js.map